/**
 * RoleAssignmentWatcher — App-level global component.
 *
 * Monitors the roles table for assignment changes to the current user.
 * Shows a full-screen pop-up when a role is assigned, then redirects.
 * Always mounted when user is authenticated (regardless of current page).
 *
 * DESIGN_BRIEF_REGISTRATION_AND_ROLES.md — Section 5.
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'

interface AssignedRole {
  id: string
  character_name: string
  country_code: string
  title: string
  sim_run_id: string
}

export function RoleAssignmentWatcher() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [popup, setPopup] = useState<AssignedRole | null>(null)
  const knownRoleRef = useRef<string | null>(null)
  const initialCheckDone = useRef(false)

  // Skip for moderators — they manage roles, don't receive them
  const isParticipant = profile?.system_role === 'participant'

  // Fetch current role assignment
  const checkMyRole = useCallback(async () => {
    if (!user?.id) return null
    const { data } = await supabase
      .from('roles')
      .select('id,character_name,country_code,title,sim_run_id')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .limit(1)

    // Find role in a non-template active/pre_start sim
    if (data && data.length > 0) {
      const role = data[0] as AssignedRole
      // Exclude template sim
      if (role.sim_run_id === '00000000-0000-0000-0000-000000000001') return null
      return role
    }
    return null
  }, [user?.id])

  // Initial check on mount — navigate if already assigned (no pop-up)
  useEffect(() => {
    if (!user?.id || !isParticipant || initialCheckDone.current) return
    initialCheckDone.current = true

    checkMyRole().then(role => {
      if (role) {
        knownRoleRef.current = role.id
        // Already assigned — navigate silently (no pop-up on page load)
        const currentPath = window.location.pathname
        const expectedPath = `/play/${role.sim_run_id}`
        if (!currentPath.startsWith(expectedPath)) {
          navigate(expectedPath, { replace: true })
        }
      }
    })
  }, [user?.id, isParticipant, checkMyRole, navigate])

  // Realtime subscription — detect new assignments
  useEffect(() => {
    if (!user?.id || !isParticipant) return

    const ch = supabase
      .channel(`role-watcher-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'roles',
      }, (payload) => {
        const newRow = payload.new as Record<string, unknown> | null
        const oldRow = payload.old as Record<string, unknown> | null

        if (!newRow) return

        // Case 1: Role assigned TO ME (new.user_id = my id)
        if (newRow.user_id === user.id) {
          const roleId = newRow.id as string
          const simRunId = newRow.sim_run_id as string
          // Skip template
          if (simRunId === '00000000-0000-0000-0000-000000000001') return
          // Is this a NEW assignment (not already known)?
          if (roleId !== knownRoleRef.current) {
            knownRoleRef.current = roleId
            setPopup({
              id: roleId,
              character_name: (newRow.character_name as string) || roleId,
              country_code: (newRow.country_code as string) || '',
              title: (newRow.title as string) || '',
              sim_run_id: simRunId,
            })
          }
        }

        // Case 2: Role REMOVED from me (old had my user_id, new doesn't)
        if (oldRow?.user_id === user.id && newRow.user_id !== user.id) {
          knownRoleRef.current = null
          // Check if reassigned to a different role in same or different sim
          checkMyRole().then(newRole => {
            if (newRole) {
              knownRoleRef.current = newRole.id
              setPopup(newRole)
            } else {
              // No role anymore — go to waiting
              navigate('/play', { replace: true })
            }
          })
        }
      })
      .subscribe()

    return () => { supabase.removeChannel(ch) }
  }, [user?.id, isParticipant, checkMyRole, navigate])

  // Handle pop-up OK — navigate with state signal for ParticipantDashboard
  const handleOk = () => {
    if (!popup) return
    const targetPath = `/play/${popup.sim_run_id}`
    setPopup(null)
    navigate(targetPath, { replace: true, state: { freshAssignment: true } })
  }

  // Don't render anything for moderators or when no popup
  if (!isParticipant || !popup) return null

  // Full-screen pop-up
  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-[fadeIn_0.3s_ease]">
        <h2 className="font-heading text-h2 text-text-primary mb-6 uppercase tracking-wider">
          Role Assignment
        </h2>
        <div className="space-y-3 mb-8">
          <div>
            <span className="font-body text-caption text-text-secondary uppercase tracking-wider">Your Role</span>
            <p className="font-heading text-h1 text-action mt-1">{popup.title || popup.character_name}</p>
          </div>
          <div>
            <span className="font-body text-caption text-text-secondary uppercase tracking-wider">Your Country</span>
            <p className="font-heading text-h2 text-text-primary mt-1 capitalize">{popup.country_code}</p>
          </div>
        </div>
        <button
          onClick={handleOk}
          className="bg-action text-white font-body text-body font-medium py-3 px-12 rounded-lg hover:opacity-90 transition-opacity"
        >
          OK
        </button>
      </div>
    </div>
  )
}
